/**
 * GlobalSearch — app-weites Suchfeld in der Topbar, linksbündig an der
 * früheren Breadcrumb-Position (2026-06-09 dorthin verschoben; vorher mittig).
 * Vorher lebte das Suchinput page-local in LibraryPage; dadurch war die Suche
 * nur auf /library erreichbar und ⌘K funktionierte nur dort. Hochgezogen ins
 * Layout (Topbar), damit die Suche von jeder Seite aus verfügbar ist.
 *
 * Verdrahtung (URL ist Single-Source-of-Truth):
 *  - Auf /library: Tippen → debounced Push von `?q=` in die URL. LibraryPage
 *    liest `?q=` und filtert live. (Verhalten wie zuvor, nur Owner gewechselt.)
 *  - Auf anderen Seiten: KEIN live-Push (würde ein fremdes ?q= auf die Route
 *    schreiben). Enter / Suggestion-Commit → navigate('/library?q=…').
 *  - ⌘K / Ctrl+K: fokussiert das Input — jetzt global (window-Listener hier).
 *  - SearchSuggestPopover ist self-fetching + location-frei → 1:1 wiederverwendet.
 *
 * Option-IDs der Suggestions sind `suggest-option-${idx}` (Konvention von
 * SearchSuggestPopover, hartcodiert) — der Keyboard-Handler matcht darauf.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'wouter';
import { I } from '../lib/icons';
import { SearchSuggestPopover } from './SearchSuggestPopover';

const SUGGEST_LISTBOX_ID = 'global-search-suggest-listbox';

export function GlobalSearch() {
  const [location, navigate] = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const onLibrary = location === '/library';
  // Nur auf /library spiegelt die URL den Suchbegriff; sonst ist das Feld
  // „leer" als Einstieg (es navigiert beim Absenden erst dorthin).
  const urlQuery = onLibrary ? (searchParams.get('q') ?? '') : '';

  const [rawQuery, setRawQuery] = useState(() =>
    location === '/library' ? (searchParams.get('q') ?? '') : '',
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<number | undefined>(undefined);

  // Suggest-Combobox-State (ARIA 1.2 Listbox-Popup).
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestActiveIdx, setSuggestActiveIdx] = useState(-1);
  const [suggestTotal, setSuggestTotal] = useState(0);

  // ⌘K / Ctrl+K — global focus (vorher page-local in LibraryPage).
  useEffect(() => {
    function handler(e: KeyboardEvent): void {
      const isCmdK = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k';
      if (!isCmdK) return;
      e.preventDefault();
      inputRef.current?.focus();
      inputRef.current?.select();
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Sync rawQuery aus der URL, wenn diese sich extern ändert (Chip-Click /
  // Filter-Zurücksetzen) — nur auf /library, nur bei echter Divergenz (kein Loop).
  useEffect(() => {
    if (onLibrary && urlQuery !== rawQuery) {
      setRawQuery(urlQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlQuery, onLibrary]);

  // Live-Filter NUR auf /library: debounce → URL-`q`-Push (replace).
  useEffect(() => {
    if (!onLibrary) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      const trimmed = rawQuery.trim();
      if (trimmed !== (searchParams.get('q') ?? '')) {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            if (trimmed) next.set('q', trimmed);
            else next.delete('q');
            return next;
          },
          { replace: true },
        );
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawQuery, onLibrary]);

  // Absenden (Enter ohne aktive Suggestion ODER Suggestion-Commit). Auf
  // /library: URL sofort setzen (Debounce überspringen). Sonst: hin-navigieren.
  function submitQuery(value: string): void {
    const trimmed = value.trim();
    clearTimeout(debounceRef.current);
    setRawQuery(value);
    setSuggestOpen(false);
    if (onLibrary) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (trimmed) next.set('q', trimmed);
          else next.delete('q');
          return next;
        },
        { replace: true },
      );
      inputRef.current?.focus();
    } else if (trimmed) {
      navigate(`/library?q=${encodeURIComponent(trimmed)}`);
    } else {
      navigate('/library');
    }
  }

  return (
    // Redesign S2 (2026-10-10): wieder ein eigenes, begrenztes Feld links in der
    // Topbar (max-w-[640px]) — rechts daneben leben Glocke + „Hochladen". Die
    // Suche bleibt das dominante Element. Focus-Ring liegt am Feld selbst
    // (focus-within), `group` färbt das Lupen-Icon. Auf Mobile sichtbar.
    // (2026-07-20 bis S2: „die Bar IST das Feld", randlos über die volle Breite.)
    <div
      data-tour="search"
      className="group relative flex items-center w-full max-w-[640px] h-10 min-w-0 rounded-control border border-ink-200 bg-ink-50 transition-colors focus-within:bg-white focus-within:border-brand-green-500 focus-within:ring-2 focus-within:ring-brand-green-500/20"
    >
      <I.search
        size={18}
        aria-hidden="true"
        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-400 transition group-focus-within:text-brand-green-700"
      />
      <input
        ref={inputRef}
        type="search"
        value={rawQuery}
        onChange={(e) => {
          setRawQuery(e.target.value);
          setSuggestOpen(true);
        }}
        onFocus={() => setSuggestOpen(true)}
        onKeyDown={(e) => {
          if (suggestOpen && suggestTotal > 0) {
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setSuggestActiveIdx((idx) => (idx + 1) % suggestTotal);
              return;
            }
            if (e.key === 'ArrowUp') {
              e.preventDefault();
              setSuggestActiveIdx((idx) => (idx <= 0 ? suggestTotal - 1 : idx - 1));
              return;
            }
            if (e.key === 'Enter' && suggestActiveIdx >= 0) {
              e.preventDefault();
              // Aktive Suggestion committen — Popover-Item-Click triggert onCommit.
              const optionEl = document.getElementById(
                `suggest-option-${suggestActiveIdx}`,
              ) as HTMLButtonElement | null;
              optionEl?.click();
              setSuggestOpen(false);
              return;
            }
            if (e.key === 'Escape') {
              e.preventDefault();
              setSuggestOpen(false);
              return;
            }
          }
          if (e.key === 'Enter') {
            e.preventDefault();
            submitQuery(rawQuery);
          } else if (e.key === 'Escape') {
            setSuggestOpen(false);
          }
        }}
        placeholder="Assets, Tags, Produkte, Kampagnen durchsuchen…"
        aria-label="Assets durchsuchen"
        role="combobox"
        aria-expanded={suggestOpen && suggestTotal > 0}
        aria-controls={SUGGEST_LISTBOX_ID}
        aria-autocomplete="list"
        aria-activedescendant={
          suggestOpen && suggestActiveIdx >= 0
            ? `suggest-option-${suggestActiveIdx}`
            : undefined
        }
        className="w-full h-full bg-transparent border-0 pl-10 pr-14 text-sm text-ink-900 placeholder:text-ink-500 focus:outline-none"
      />
      <kbd
        className="hidden md:inline-flex absolute right-2.5 top-1/2 -translate-y-1/2 items-center gap-0.5 px-1.5 py-0.5 bg-white border border-ink-200 rounded text-2xs font-mono text-ink-500 pointer-events-none"
        aria-hidden="true"
      >
        <span>⌘</span>
        <span>K</span>
      </kbd>
      {suggestOpen && (
        <SearchSuggestPopover
          query={rawQuery}
          listboxId={SUGGEST_LISTBOX_ID}
          onCommit={(label) => submitQuery(label)}
          onClose={() => setSuggestOpen(false)}
          activeIndex={suggestActiveIdx}
          setActiveIndex={setSuggestActiveIdx}
          onTotalChange={setSuggestTotal}
        />
      )}
    </div>
  );
}
